<template>
  <Table.Base :id="TableTypes.Archive" class="h-full tablet-h:pl-[30px]">
    <Table.Headers>
      <Table.Header class="cursor-pointer" @click="handleSort('name')">
        <span class="font-primary">Name</span>
        <Icon
          name="ArrowUp"
          :size="12"
          class="stroke-gray-800 ml-2"
          :class="headerIconClasses"
        />
      </Table.Header>
      <Table.Header class="hidden tablet-h:block min-w-[200px]">
        <span class="font-primary">Archived Date</span>
      </Table.Header>
      <Table.Header class="hidden tablet-h:block min-w-[200px]">
        <span class="font-primary">Archived Action</span>
      </Table.Header>
      <Table.Header class="tablet-h:hidden" />
    </Table.Headers>

    <template v-if="showSkeleton">
      <Table.Row v-for="n in 4" :key="n" :id="n.toString()">
        <Table.Cell>
          <div class="w-6 h-6 flex items-center justify-center mr-2.5">
            <Skeleton class="w-[17px] h-6" />
          </div>
          <div>
            <Skeleton class="w-[85px] h-[13px] mb-1" />
            <Skeleton class="tablet-h:hidden w-[150px] h-[13px]" />
          </div>
        </Table.Cell>
        <Table.Cell class="hidden tablet-h:flex">
          <Skeleton class="w-[85px] h-[13px]" />
        </Table.Cell>
        <Table.Cell class="hidden tablet-h:flex">
          <Skeleton class="w-[85px] h-[13px]" />
        </Table.Cell>
      </Table.Row>
    </template>

    <template v-else>
      <div v-if="!items?.length" class="overflow-y-auto h-full">
        <div class="w-full h-full flex flex-col items-center justify-center">
          <div
            class="size-[126px] flex items-center justify-center bg-gray-200/30 rounded-[16px] mb-[43px]"
          >
            <Lottie name="Archived-Files" class="min-w-[160px]" />
          </div>

          <div
            class="font-semibold font-primary text-[20px] leading-[20px] tracking-[-0.25px] text-gray-800 mb-4"
          >
            No archived copyrights
          </div>

          <div
            class="font-secondary text-[14px] leading-[22px] text-gray-800 max-w-[302px] text-center"
          >
            Files you no longer wish to copyright protect will appear here
          </div>
        </div>
      </div>

      <div v-else class="h-full overflow-y-auto">
        <Table.Row
          v-for="item in props.items"
          :key="item.id"
          :id="item.id"
          @click="handleClick(item.id)"
        >
          <Table.Cell>
            <ThumbnailIcon
              class="mr-2.5 w-9 h-9 tablet-h:w-6 tablet-h:h-6"
              :icon-size="thumbnailSize"
              :type="item.type"
              :image-url="
                applyAuthorizationToken(item.imageUrl, [
                  { key: 'width', value: 36 },
                ])
              "
            />
            <div v-truncate class="w-full">
              <div data-truncate-target class="font-primary">
                {{ item.fileName }}
              </div>
              <div
                class="font-secondary tablet-h:hidden text-[13px] opacity-50 mt-0.5"
              >
                Archived on {{ formatTimestamp(item.archivedAt) }}
              </div>
            </div>
          </Table.Cell>
          <Table.Cell class="hidden tablet-h:flex">
            <span class="font-secondary">
              {{ formatTimestamp(item.archivedAt) }}
            </span>
          </Table.Cell>
          <Table.Cell class="hidden tablet-h:flex">
            <span class="font-secondary">{{ item.status }}</span>
          </Table.Cell>
          <Table.Cell class="tablet-h:hidden">
            <Icon
              name="ThreeDots"
              class="stroke-gray-800 fill-gray-800 mr-2"
              :size="18"
              @click="handleMoreClick(item)"
            />
          </Table.Cell>
        </Table.Row>
      </div>
    </template>
  </Table.Base>
</template>

<script setup lang="ts">
import { Table, ThumbnailIcon, Skeleton, Icon } from '@dacr/ui'
import type { ArchivedFile } from '@/shared/graphql/legacy/generated'
import { formatTimestamp } from '@/shared/utils/datetime'
import { applyAuthorizationToken } from '@/shared/utils/url'
import { useDevice } from '@/shared/composables/useDevice'
import { computed, onBeforeUnmount, watchEffect, ref } from 'vue'
import Lottie from '@/shared/components/Lottie.vue'
import { TableTypes } from '@/shared/types'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'

const props = defineProps<{
  items?: ArchivedFile[]
}>()
const emit = defineEmits<{
  sort: [value: [string, 'asc' | 'desc']]
  scroll: [event: Event]
}>()

const device = useDevice()
const { open } = useDialog()
const table = Table.useTable(TableTypes.Archive)

const container = ref()
const sortKey = ref('name')
const sortDirection = ref<'asc' | 'desc'>('asc')

const headerIconClasses = computed(() => ({
  ...(sortDirection.value === 'desc' && { 'rotate-180': true }),
}))

const handleSort = (key: string) => {
  sortKey.value = key
  sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
  emit('sort', [sortKey.value, sortDirection.value])
}

const showSkeleton = computed(() => props.items === undefined)

const thumbnailSize = computed(() =>
  device.isDesktop.value || device.isTabletHorizontal.value ? 24 : 36
)

const handleScroll = (event: Event) => {
  emit('scroll', event)
}

const handleClick = (id: string) => {
  if (device.isMobile.value || device.isTabletVertical.value) return

  if (!table.selectedRows.value.includes(id)) {
    table.resetSelection()
  }

  table.toggleRow(id)
}

const handleMoreClick = (item: ArchivedFile) => {
  open(Dialogs.ArchiveMenu, {
    props: { item },
  })
}

watchEffect(() => {
  container.value?.$el.addEventListener('scroll', handleScroll)
})

onBeforeUnmount(() => {
  container.value?.$el.removeEventListener('scroll', handleScroll)
})
</script>
